/** @jsxImportSource @emotion/react */
import React, { useEffect, useRef, useState } from 'react';
import styled from '@emotion/styled';
import * as font from '@constant/font';
import * as color from '@constant/color';
import * as size from '@constant/size';
import { IBlock } from '@datatype/type';
import BoxBlock from '@component/common/block/box-block';
import { css } from '@emotion/react';
import { useClicker } from '@hook/common/useEvent';
import DOMPurify from 'dompurify';
import { parseFurigana } from '@util/string';
import { BLANK_MARKER } from '@constant/variable';
import { v4 as uuid4 } from 'uuid';

type TAnnotationLayout = 'distribute' | 'start' | 'center';

interface IProps {
  text: string;
  annotation?: { reading: string; color?: string };
  // 같은 컨테이너에 annotation 있는 TextBlock과 함께 배치될 때, annotation이 없어도 빈 자리를 잡아 정렬 유지
  reserveAnnotationSpace?: boolean;
  // reading과 base의 길이 차이 처리 방식
  //  - 'distribute' (기본): 짧은 쪽이 긴 쪽 폭에 글자 단위로 균등 분포
  //     (space-around: 양끝에도 절반 간격 여백. space-between 아님 — 가장자리에서 살짝 안쪽)
  //  - 'start': 둘 다 왼쪽 정렬
  //  - 'center': 둘 다 중앙 정렬
  annotationLayout?: TAnnotationLayout;
  blank?: {
    textColor?: string; // 지정되면 답 노출 상태로 간주 → reading 표시
    boxColor?: string;
    underlineColor?: string;
  };
  design: Pick<IBlock['design'], 'box' | 'text'>;
  click?: IBlock['click'];
}

/**
 * 텍스트의 한 토큰 — 한자/히라가나/문자의 한 조각. 빈칸 여부는 소속 그룹(ITokenGroup.blankIdx)으로 판단.
 *
 * 예시 — text="準備###万端###じゃん", reading="じゅんびばんたんじゃん"
 *   { base: "準備",  annotation: "じゅんび" }
 *   { base: "万端",  annotation: "ばんたん" }
 *   { base: "じゃん", annotation: undefined }
 */
interface IToken {
  base: string;
  annotation?: string;
}

const Text = styled.div<{ design: IProps['design']['text'] }>`
  // font
  font: ${(props) => {
    switch (props.design?.size) {
      case 'tiny':
        return `${font.FONT_TINY_400}`;
      case 'small':
        return `${font.FONT_SMALL_400}`;
      case 'medium':
        return `${font.FONT_MEDIUM_400}`;
      case 'large':
        return `${font.FONT_LARGE_400}`;
      case 'big':
        return `${font.FONT_BIG_400}`;
      case 'huge':
        return `${font.FONT_HUGE_400}`;
      case 'giant':
        return `${font.FONT_GIANT_400}`;
      default:
    }
  }};
  ${(props) =>
    props.design?.custom?.fontSize !== undefined &&
    css`
      font-size: ${props.design.custom.fontSize}px;
      line-height: normal;
    `}
  color: ${(props) => props.design?.color || color.BLACK_2};
  font-weight: ${(props) => (props.design?.bold ? '600' : '400')};
  text-decoration: ${(props) => (props.design?.underline ? 'underline' : 'none')};
  text-decoration-color: ${(props) => props.design?.underlineColor ?? props.design?.color};

  u {
    color: ${(props) => props.design?.underlineColor ?? props.design?.color};
    text-decoration-color: ${(props) => props.design?.underlineColor ?? props.design?.color};
  }

  // oneline (text로 넘기기) (width 설정해야할수도)
  ${(props) =>
    props.design?.oneline &&
    css`
      display: inline-block;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      width: 100%;
      min-width: 0;
    `} // multiline (text로 넘기기)
    ${(props) =>
    props.design?.multiline &&
    css`
      display: -webkit-box;
      -webkit-line-clamp: ${props.design.multiline};
      -webkit-box-orient: vertical;
      -webkit-box-align: start;
      overflow: hidden;
      text-overflow: ellipsis;
      width: 100%;
    `}

    ${(props) =>
    props?.design?.event?.hover &&
    css`
      &:hover {
        ${props.design.event?.hover}
      }
    `}
    ${(props) =>
    !props.design?.oneline &&
    css`
      white-space: pre-wrap;
      overflow-wrap: break-word;
      /* word-break: break-all; */
      word-break: normal;
    `}
`;
// TODO: white-space 보기

// 최상위 flex row 컨테이너: 토큰을 wrap, 베이스 하단 기준 정렬 (후리가나 공간 포함)
// oneline + 잘림 시 우측 끝에 ellipsis(…) 인디케이터 표시 (JS로 잘림 감지 후 isOverflowing 전달)
const TextContainer = styled.div<{ oneline?: boolean; isOverflowing?: boolean }>`
  display: flex;
  flex-flow: row ${(props) => (props.oneline ? 'nowrap' : 'wrap')};
  align-items: last baseline;
  column-gap: 0;
  row-gap: 0.2em;
  ${(props) =>
    props.oneline &&
    css`
      overflow: hidden;
      max-width: 100%;
      position: relative;
    `}
  ${(props) =>
    props.oneline &&
    props.isOverflowing &&
    css`
      &::after {
        content: '…';
        position: absolute;
        right: 0;
        top: 0;
        bottom: 0;
        display: flex;
        align-items: center;
        padding: 0 0.1em 0 0.1em;
        background-color: white;
        color: inherit;
        font-size: inherit;
        pointer-events: none;
      }
    `}
`;

// 일반 토큰 / 빈칸 박스 공통 래퍼: 위(reading) + 아래(base) column 스택
// layout에 따라 자식 정렬/폭 제어. distribute는 stretch로 reading/base가 max 폭까지 늘어나고
// 내부에서 space-around로 글자 균등 분포.
const TokenWrapper = styled.div<{ layout: TAnnotationLayout; blank?: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: ${(props) => {
    switch (props.layout) {
      case 'start':
        return 'flex-start';
      case 'center':
        return 'center';
      case 'distribute':
        return 'stretch';
    }
  }};
  ${(props) =>
    props.blank &&
    css`
      margin: 0 0.2em;
    `}
`;

const READING_ROW_FONT_SIZE = '0.65em';
const READING_ROW_LINE_HEIGHT = 1;

// 베이스 위에 얹히는 후리가나(annotation/reading) 행 — 실제 공간을 차지
const ReadingRow = styled.div<{ layout: TAnnotationLayout; color?: string }>`
  font-size: ${READING_ROW_FONT_SIZE};
  line-height: ${READING_ROW_LINE_HEIGHT};
  white-space: nowrap;
  opacity: 0.6;
  color: ${(props) => props.color};
  ${(props) =>
    props.layout === 'distribute' &&
    css`
      display: flex;
      justify-content: space-around;
    `}
`;

// 평문 베이스 행 — distribute일 때만 글자 균등 분포
const PlainBaseRow = styled.div<{ layout: TAnnotationLayout }>`
  ${(props) =>
    props.layout === 'distribute' &&
    css`
      display: flex;
      justify-content: space-around;
    `}
`;

// 빈칸 박스 본체 — sub-token 셀들을 flex row로 배치
const BlankBaseRow = styled.div<{
  textColor?: string;
  boxColor?: string;
  underlineColor?: string;
}>`
  display: ${(props) => (props.underlineColor ? 'inline-flex' : 'flex')};
  ${(props) =>
    props.underlineColor &&
    css`
      line-height: 1;
    `}
  padding: ${(props) => (props.underlineColor ? 0 : '0 0.3em')};
  border-radius: ${(props) => (props.underlineColor ? 0 : size.BORDER_RADIUS_1)};
  background-color: ${(props) => (props.underlineColor ? 'transparent' : (props.boxColor ?? '#ffffff'))};
  color: ${(props) => props.textColor ?? 'transparent'};
  border: ${(props) => (props.underlineColor ? 'none' : `1px solid ${color.GRAY_2}`)};
  ${(props) =>
    props.underlineColor &&
    css`
      border-bottom: 1.5px solid ${props.underlineColor};
    `}
`;

// 빈칸 reading 행 — sub-token 셀들을 flex row로 배치
const BlankReadingRow = styled.div<{ textColor?: string }>`
  font-size: ${READING_ROW_FONT_SIZE};
  line-height: ${READING_ROW_LINE_HEIGHT};
  white-space: nowrap;
  color: ${(props) => props.textColor};
  display: flex;
`;

// 빈칸 내부 sub-token 셀 — base 길이 비례 flex, reading과 base 수평 정렬
const BlankSubCell = styled.div<{ grow: number; layout: TAnnotationLayout }>`
  flex: ${(props) => props.grow};
  display: flex;
  justify-content: ${(props) => (props.layout === 'distribute' ? 'space-around' : 'center')};
`;

// distribute 모드에서는 글자 단위 span으로 쪼개야 space-around가 작동
const renderChars = (text: string, layout: TAnnotationLayout) =>
  layout === 'distribute' ? Array.from(text).map((ch) => <span key={uuid4()}>{ch}</span>) : text;

interface ITokenGroup {
  blankIdx: number; // -1 일반, 0+ 빈칸 (몇 번째 [A]인지)
  tokens: IToken[];
}

// 문장 전체의 빈칸 위치. cleanText(###제거) 기준 절대 인덱스 범위 목록.
// 예: "準備###万端###じゃん" → [[2, 4]]
const computeSentenceBlankRange = (text: string): Array<[number, number]> => {
  const ranges: Array<[number, number]> = [];
  let cursor = 0;
  text.split(BLANK_MARKER).forEach((seg, i) => {
    if (i % 2 === 1) ranges.push([cursor, cursor + seg.length]);
    cursor += seg.length;
  });
  return ranges;
};

// `<u>…</u>`를 제외한 실제 텍스트 기준으로 밑줄 범위를 계산한다.
// 빈칸 마커는 화면 글자가 아니므로 인덱스에 포함하지 않는다.
const computeUnderlineRanges = (text: string): Array<[number, number]> => {
  const ranges: Array<[number, number]> = [];
  let cursor = 0;
  let start: number | null = null;

  text.split(/(<\/?u>)/g).forEach((part) => {
    if (part === '<u>') {
      start = cursor;
      return;
    }
    if (part === '</u>') {
      if (start !== null) ranges.push([start, cursor]);
      start = null;
      return;
    }
    cursor += part.split(BLANK_MARKER).join('').length;
  });

  return ranges;
};

// 한 토큰 내부에서 빈칸 경계로 쪼갤 cut point들. 양끝 포함, 내부에 경계 걸치면 그 지점 추가.
// 예: 토큰(0~4), blankRanges=[[2,4]] → [0, 2, 4]
const computeTokenBlankRange = (start: number, end: number, blankRanges: Array<[number, number]>): number[] => {
  const cuts = [start, end];
  for (const [bs, be] of blankRanges) {
    if (bs > start && bs < end) cuts.push(bs);
    if (be > start && be < end) cuts.push(be);
  }
  return cuts.sort((a, b) => a - b);
};

// text와 reading으로 토큰 그룹 빌드.
// - parseFurigana 토큰(한자 블록 단위)이 빈칸 경계를 가로지르면 → 경계에서 쪼개고 ruby를 글자수 비례로 분배
//   (숙자훈의 경우 부정확하지만 근사치로 허용)
// - 가로지르지 않으면 ruby 유지
// - 연속된 같은 blankIdx 조각은 한 group으로 묶음
// - annotation 없는 평문은 마지막에 공백 단위로 추가 분해 → 영어 등 단어 단위 wrap 허용
const buildTokenGroups = (text: string, reading?: string): ITokenGroup[] => {
  const sourceText = text.replace(/<\/?u>/g, '');
  const cleanText = sourceText.split(BLANK_MARKER).join('');
  const blankRanges = computeSentenceBlankRange(sourceText);
  const underlineRanges = computeUnderlineRanges(text);
  const findBlankIdx = (pos: number): number => blankRanges.findIndex(([s, e]) => pos >= s && pos < e);

  // 평문 토큰을 줄바꿈 가능한 단위로 분해한다. 일본어 가나는 글자 단위, 공백이 있는 언어는 단어 단위로 나눈다.
  // flex item 내 공백은 폭 0으로 죽으므로 공백은 nbsp로 치환해 직전 단어에 합친다.
  // → 줄바꿈은 토큰 사이에서만 발생하므로 줄 시작에 공백이 따로 보이지 않음.
  //
  // 예시 — "keep an extra"
  //   ✗ 단일 토큰:           <keep an extra>                       → flex item 1개, wrap 불가
  //   ✗ 단어+공백 분리:      <keep><·><an><·><extra>               → 공백 토큰이 flex에서 폭 0으로 죽음 → "keepanextra"
  //   ✓ 공백을 nbsp로 합침:  <keep·><an·><extra>                   → 각 토큰이 자기 폭 유지, wrap 가능
  const splitPlainTokensForWrap = (g: ITokenGroup): ITokenGroup => ({
    ...g,
    tokens: g.tokens.flatMap((t) => {
      if (t.annotation) return [t];
      const underlined = t.base.startsWith('<u>') && t.base.endsWith('</u>');
      const plainBase = underlined ? t.base.slice(3, -4) : t.base;
      const toToken = (base: string): IToken => ({ base: underlined ? `<u>${base}</u>` : base });
      if (/^[\p{Script=Hiragana}\p{Script=Katakana}ー。、！？]+$/u.test(plainBase)) {
        return Array.from(plainBase).map(toToken);
      }
      return (plainBase.match(/\S+\s*/g) ?? []).map((base) => toToken(base.replace(/\s+$/, '\u00A0')));
    }),
  });

  const rawTokens = reading ? parseFurigana(cleanText, reading) : [{ text: cleanText, ruby: undefined }];

  const groups: ITokenGroup[] = [];
  const pushToken = (base: string, annotation: string | undefined, blankIdx: number, underline: boolean) => {
    const last = groups[groups.length - 1];
    const token: IToken = { base: underline ? `<u>${base}</u>` : base, annotation };
    if (last && last.blankIdx === blankIdx) last.tokens.push(token);
    else groups.push({ blankIdx, tokens: [token] });
  };

  let cursor = 0;
  for (const raw of rawTokens) {
    const start = cursor;
    const end = cursor + raw.text.length;
    cursor = end;

    const cuts = Array.from(
      new Set([
        ...computeTokenBlankRange(start, end, blankRanges),
        ...underlineRanges.flatMap(([rangeStart, rangeEnd]) => [rangeStart, rangeEnd].filter((value) => value > start && value < end)),
      ]),
    ).sort((a, b) => a - b);
    const straddles = cuts.length > 2;
    const textLen = raw.text.length;
    const rubyLen = raw.ruby?.length ?? 0;

    for (let i = 0; i < cuts.length - 1; i++) {
      const base = raw.text.substring(cuts[i] - start, cuts[i + 1] - start);
      // 경계 걸친 ruby는 글자수 비례로 쪼개 각 조각에 배분 (숙자훈엔 부정확하지만 근사)
      const annotation = straddles
        ? raw.ruby
          ? raw.ruby.substring(
              Math.round(((cuts[i] - start) / textLen) * rubyLen),
              Math.round(((cuts[i + 1] - start) / textLen) * rubyLen),
            )
          : undefined
        : raw.ruby;
      const underline = underlineRanges.some(([rangeStart, rangeEnd]) => cuts[i] >= rangeStart && cuts[i + 1] <= rangeEnd);
      pushToken(base, annotation, findBlankIdx(cuts[i]), underline);
    }
  }
  return groups.map((g) => (g.blankIdx >= 0 ? g : splitPlainTokensForWrap(g)));
};

// 일반 토큰 — 평문 위에 (있으면) 후리가나
const PlainToken: React.FC<{
  token: IToken;
  reserveAnnotationSpace: boolean;
  annotationColor?: string;
  layout: TAnnotationLayout;
}> = ({
  token,
  reserveAnnotationSpace,
  annotationColor,
  layout,
}) => (
  <TokenWrapper layout={layout}>
    {(token.annotation || reserveAnnotationSpace) && (
      <ReadingRow layout={layout} color={annotationColor}>
        {renderChars(token.annotation ?? '\u00A0', layout)}
      </ReadingRow>
    )}
    <PlainBaseRow layout={layout}>
      {token.base.includes('<u>') ? (
        <span dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(token.base) }} />
      ) : (
        renderChars(token.base, layout)
      )}
    </PlainBaseRow>
  </TokenWrapper>
);

// 일반 그룹 — 토큰들을 순차 렌더
const PlainTokenGroup: React.FC<{
  group: ITokenGroup;
  reserveAnnotationSpace: boolean;
  annotationColor?: string;
  layout: TAnnotationLayout;
}> = ({
  group,
  reserveAnnotationSpace,
  annotationColor,
  layout,
}) => (
  <>
    {group.tokens.map((token) => (
      <PlainToken
        key={uuid4()}
        token={token}
        reserveAnnotationSpace={reserveAnnotationSpace}
        annotationColor={annotationColor}
        layout={layout}
      />
    ))}
  </>
);

// 빈칸 그룹 — 하나의 빈칸 박스로 합쳐 렌더. sub-token별 셀 컬럼으로 reading/base 수평 정렬
const BlankTokenGroup: React.FC<{ group: ITokenGroup; blank?: IProps['blank']; layout: TAnnotationLayout }> = ({
  group,
  blank,
  layout,
}) => {
  const showReading = !!blank?.textColor && group.tokens.some((t) => t.annotation);
  return (
    <TokenWrapper layout={layout} blank>
      {showReading && (
        <BlankReadingRow textColor={blank?.textColor}>
          {group.tokens.map((t) => (
            <BlankSubCell key={uuid4()} grow={t.base.length} layout={layout}>
              {renderChars(t.annotation ?? '', layout)}
            </BlankSubCell>
          ))}
        </BlankReadingRow>
      )}
      <BlankBaseRow textColor={blank?.textColor} boxColor={blank?.boxColor} underlineColor={blank?.underlineColor}>
        {group.tokens.map((t) => (
          <BlankSubCell key={uuid4()} grow={t.base.length} layout={layout}>
            {renderChars(t.base, layout)}
          </BlankSubCell>
        ))}
      </BlankBaseRow>
    </TokenWrapper>
  );
};

const TextBlock: React.FC<IProps> = ({
  text,
  annotation,
  reserveAnnotationSpace,
  annotationLayout,
  blank,
  design,
  click,
}) => {
  const clicker = useClicker(click?.handler, click?.delay, click?.mode);
  const hasBlank = text.includes(BLANK_MARKER);
  const isStructuredMode = !!annotation?.reading || hasBlank || !!reserveAnnotationSpace;
  const layout: TAnnotationLayout = annotationLayout ?? 'distribute';

  // oneline 잘림 감지 — scrollWidth > clientWidth면 우측 끝에 … 인디케이터 표시
  const containerRef = useRef<HTMLDivElement>(null);
  const [isOverflowing, setIsOverflowing] = useState(false);
  useEffect(() => {
    if (!design.text?.oneline) return;
    const el = containerRef.current;
    if (!el) return;
    const check = () => setIsOverflowing(el.scrollWidth > el.clientWidth + 1);
    check();
    const observer = new ResizeObserver(check);
    observer.observe(el);
    return () => observer.disconnect();
  }, [design.text?.oneline, text]);

  if (isStructuredMode) {
    const groups = buildTokenGroups(text, annotation?.reading);
    // structured 모드는 내부 flex(TextContainer)로 줄바꿈을 처리하므로 multiline ellipsis 스타일과 충돌 — multiline 제거
    const structuredTextDesign = design.text ? { ...design.text, multiline: undefined } : design.text;
    return (
      <BoxBlock design={{ box: design.box }} onClick={clicker}>
        <Text design={structuredTextDesign}>
          <TextContainer ref={containerRef} oneline={design.text?.oneline} isOverflowing={isOverflowing}>
            {groups.map((group) =>
              group.blankIdx >= 0 ? (
                <BlankTokenGroup key={uuid4()} group={group} blank={blank} layout={layout} />
              ) : (
                <PlainTokenGroup
                  key={uuid4()}
                  group={group}
                  reserveAnnotationSpace={!!reserveAnnotationSpace}
                  annotationColor={annotation?.color}
                  layout={layout}
                />
              ),
            )}
          </TextContainer>
        </Text>
      </BoxBlock>
    );
  }

  return (
    <BoxBlock design={{ box: design.box }} onClick={clicker}>
      <Text design={design.text} dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(text) }} />
    </BoxBlock>
  );
};

export default TextBlock;
